import { useEffect, useState } from 'react';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';

/**
 * Global top-of-viewport progress slider mounted once in AppLayout.
 *
 * Visible while any @tanstack/react-query fetch/mutation is in flight (works
 * across every MFE since they all share the shell's react-query provider via
 * module-federation) AND while any imperative `fetch()` call is outstanding.
 * The imperative counter catches MFEs that do not use react-query for a given
 * request (e.g. axios / plain fetch).
 */
let imperativeCount = 0;
const imperativeListeners = new Set<() => void>();

function notifyImperative(): void {
  for (const l of imperativeListeners) l();
}

// Wrap window.fetch exactly once so every fetch — from any MFE — bumps the
// counter. Skip if already wrapped (hot reload / double-import).
if (typeof window !== 'undefined' && !(window as any).__esgFetchWrapped) {
  const orig = window.fetch.bind(window);
  window.fetch = (...args: Parameters<typeof fetch>) => {
    imperativeCount += 1;
    notifyImperative();
    return orig(...args).finally(() => {
      imperativeCount = Math.max(0, imperativeCount - 1);
      notifyImperative();
    });
  };
  (window as any).__esgFetchWrapped = true;
}

function useImperativeFetching(): number {
  const [, force] = useState(0);
  useEffect(() => {
    const l = () => force((n) => n + 1);
    imperativeListeners.add(l);
    return () => {
      imperativeListeners.delete(l);
    };
  }, []);
  return imperativeCount;
}

export function ProgressBar(): JSX.Element | null {
  const fetching = useIsFetching();
  const mutating = useIsMutating();
  const imperative = useImperativeFetching();
  const isActive = fetching > 0 || mutating > 0 || imperative > 0;

  if (!isActive) return null;

  return (
    <div
      role="progressbar"
      aria-label="Loading"
      className="fixed top-0 left-0 right-0 h-[3px] bg-blue-500/25 z-[1000] overflow-hidden pointer-events-none"
    >
      <div className="h-full w-1/3 bg-blue-600 animate-[esg-progress_1.4s_ease-in-out_infinite]" />
      <style>{`
        @keyframes esg-progress {
          0%   { transform: translateX(-100%); }
          50%  { transform: translateX(150%); }
          100% { transform: translateX(300%); }
        }
      `}</style>
    </div>
  );
}

export default ProgressBar;
